import { ApiHttpClient } from "../../common/http/api-http-client";
import { ResourceBaseService } from "../../common/resource-base/resource-base.service";
import { UserDto } from "./user.dto";
import { CreateUserDto } from "./create-user.dto";
import { UpdateUserDto } from "./update-user.dto";
import { IRequest } from "@/interfaces/IRequest";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { CondOperator } from "@nestjsx/crud-request";
import { plainToInstance } from "class-transformer";
import { HttpRequest } from "@/core/common/http/http-request";
import { CacheService } from "@/core/common/storage/cache.service";
import { OptionType } from "@/types/OptionType";
import { ArchivedEnum, PermissionEnum } from "@altertec_gparn/lib";
import { getOptionLoader } from "@/core/common/presenters/optionLoader";
import { UpdateUserAccessDto } from "@/core/features/users/update-user-access.dto";
import { cloneDeep, merge } from "lodash";
import { QualificationDto } from "@/core/features/qualifications/qualification.dto";
import { UpdateNotificationPreferencesDto } from "@/core/features/users-have-notification-preferences/update-notification-preferences.dto";
import { UserSessionDto } from "@/core/features/user-sessions/user-session.dto";
import { UpdateUserHiringDataDto } from "@/core/features/users/update-user-hiring-data.dto";
import { UpdateTechnicianCostsDto } from "@/core/features/users/update-technician-costs.dto";
import { UserRecordDto } from "@/core/features/users/user-record.dto";

export class UsersResource extends ResourceBaseService<UserDto, CreateUserDto, UpdateUserDto, UserRecordDto> {
  protected EntityDto = UserDto;
  protected CreateEntityDto = CreateUserDto;
  protected UpdateEntityDto = UpdateUserDto;
  protected RecordEntityDto = UserRecordDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient, protected readonly cacheService: CacheService) {
    super(apiHttpClient, "/users");
  }

  async getMe(forceRefresh?: boolean): Promise<UserDto> {
    let myUser = this.cacheService.get("myUser") as UserDto;
    if (myUser && !forceRefresh) return plainToInstance(this.EntityDto, myUser);
    myUser = await this.getMeByApi();
    this.cacheService.save("myUser", myUser);
    return plainToInstance(this.EntityDto, myUser);
  }

  async getEmployees(request: IRequest, includeArchived: boolean): Promise<FindManyResult<UserDto>> {
    const req = cloneDeep(request);
    if (includeArchived != null && !req.filters?.archived) {
      req.filters = Object.assign(req.filters || {}, {
        archived: includeArchived ? ArchivedEnum.ALL : ArchivedEnum.ONLY_ACTIVE,
      });
    }

    return this.apiHttpClient
      .request<null, FindManyResult<UserDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/employees",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(req).query(),
        })
      )
      .then((findManyResult) => {
        findManyResult.data = plainToInstance(UserDto, findManyResult.data);
        return findManyResult;
      });
  }

  private async getMeByApi(): Promise<UserDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/me",
        method: "GET",
        auth: true,
      })
    );
  }

  public async getIdleUsers(date: string, request: IRequest): Promise<FindManyResult<UserDto>> {
    return this.apiHttpClient
      .request<CreateUserDto, FindManyResult<UserDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/idle/" + date,
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((findManyResult) => {
        findManyResult.data = plainToInstance(UserDto, findManyResult.data);
        return findManyResult;
      });
  }

  public async optionLoaderByRole(
    role: PermissionEnum,
    inputValue: string,
    includeArchived?: boolean
  ): Promise<OptionType[]> {
    const request: IRequest = {
      customAndFilters: [{ field: "role.permissions", value: role, operator: CondOperator.CONTAINS }],
      join: ["role"],
    };
    return this.optionLoader(inputValue, includeArchived, request);
  }

  public async optionLoader(
    inputValue: string,
    includeArchived?: boolean,
    customRequest?: IRequest
  ): Promise<OptionType[]> {
    return getOptionLoader(
      inputValue,
      ["id", "username", "firstName", "lastName", "idWinda"],
      [{ field: "username", order: "ASC" }],
      (request) => this.findAll(customRequest ? merge(request, customRequest) : request),
      ["username"],
      {},
      includeArchived
    );
  }

  async updateUserAccess(id: string, user: UpdateUserAccessDto): Promise<UserDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/access",
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateUserAccessDto, { ...user, id: id }, { excludeExtraneousValues: true }),
      })
    );
  }

  async updateHiringData(userId: string, body: UpdateUserHiringDataDto): Promise<UserDto> {
    return this.apiHttpClient.request<UpdateUserHiringDataDto, UserDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + userId + "/hiring-data",
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateUserHiringDataDto, body, { excludeExtraneousValues: true }),
      })
    );
  }

  public async updatePhoto(id: string, photoUrl?: string) {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/photo",
        method: "PATCH",
        auth: true,
        body: { photoUrl },
      })
    );
  }

  getAvailableQualifications(id: string, request: IRequest = {}): Promise<QualificationDto[]> {
    const req = cloneDeep(request);
    req.searchBy = ["id", "name"];
    req.sortBy = "name";
    return this.apiHttpClient
      .request<null, QualificationDto[]>(
        HttpRequest.create({
          url: this.resourceUrl + "/" + id + "/available-qualifications",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(req).query(),
        })
      )
      .then((results) => {
        return plainToInstance(QualificationDto, results);
      });
  }

  async updateNotificationPreferences(body: UpdateNotificationPreferencesDto) {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/notification-preferences",
        method: "POST",
        auth: true,
        body: body,
      })
    );
  }

  async updateTechnicianCosts(body: UpdateTechnicianCostsDto): Promise<{ status: "waiting"; jobId: string }> {
    return this.apiHttpClient.request<UpdateTechnicianCostsDto, { status: "waiting"; jobId: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/update-technician-costs",
        method: "POST",
        auth: true,
        body: plainToInstance(UpdateTechnicianCostsDto, body, { excludeExtraneousValues: true }),
      })
    );
  }

  async exportEmployeesExcel(request: IRequest): Promise<{ status: "queued"; jobId: string }> {
    return this.apiHttpClient.request<null, { status: "queued"; jobId: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/employees/excel/export",
        method: "GET",
        auth: true,
        query: this.getQueryBuilder(request).query(),
      })
    );
  }

  async changeOwnPassword(currentPassword: string | undefined, newPassword: string): Promise<void> {
    const body: { currentPassword?: string; newPassword: string } = { newPassword };
    if (currentPassword) {
      body.currentPassword = currentPassword;
    }

    return this.apiHttpClient.request<{ currentPassword?: string; newPassword: string }, void>(
      HttpRequest.create({
        url: this.resourceUrl + "/me/change-password",
        method: "PATCH",
        auth: true,
        body: body,
      })
    );
  }

  async expirePassword(userId: string): Promise<void> {
    return this.apiHttpClient.request<null, void>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + userId + "/expire-password",
        method: "PATCH",
        auth: true,
      })
    );
  }

  async requireMfa(userId: string): Promise<UserDto> {
    return this.apiHttpClient.request<null, UserDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + userId + "/mfa/require",
        method: "PATCH",
        auth: true,
      })
    );
  }

  async disableMfa(userId: string): Promise<UserDto> {
    return this.apiHttpClient.request<null, UserDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + userId + "/mfa/disable",
        method: "PATCH",
        auth: true,
      })
    );
  }

  async resetMfa(userId: string): Promise<UserDto> {
    return this.apiHttpClient.request<null, UserDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + userId + "/mfa/reset",
        method: "PATCH",
        auth: true,
      })
    );
  }

  async getUserSessions(userId: string, request: IRequest): Promise<FindManyResult<UserSessionDto>> {
    return this.apiHttpClient
      .request<null, FindManyResult<UserSessionDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/" + userId + "/sessions",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => ({
        ...result,
        data: plainToInstance(UserSessionDto, result.data),
      }));
  }

  async getActiveUserSessions(userId: string, request: IRequest): Promise<FindManyResult<UserSessionDto>> {
    return this.apiHttpClient
      .request<null, FindManyResult<UserSessionDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/" + userId + "/sessions/active",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => ({
        ...result,
        data: plainToInstance(UserSessionDto, result.data),
      }));
  }

  async closeSession(userId: string, sessionId: string): Promise<void> {
    return this.apiHttpClient.request<null, void>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + userId + "/sessions/" + sessionId,
        method: "DELETE",
        auth: true,
      })
    );
  }

  async closeAllSessions(userId: string): Promise<void> {
    return this.apiHttpClient.request<null, void>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + userId + "/sessions",
        method: "DELETE",
        auth: true,
      })
    );
  }

  async importEmployees(file: File): Promise<{ jobId: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.apiHttpClient.request<FormData, { jobId: string }>(
      HttpRequest.create<FormData>({
        url: this.resourceUrl + "/employees/import",
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );
  }

  async downloadEmployeesImportTemplate(): Promise<void> {
    return this.downloadExcel(null, "/employees/import/template", "plantilla-empleados");
  }
}
